import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import { typography } from '../../../styles/typography.js';
import Card from '../../ui/card.js';
import { inventoryDashboardApi } from './Inventory_API.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const parseSelectedMonthString = selectedMonth => {
  if (!selectedMonth || typeof selectedMonth !== 'string') {
    return null;
  }
  const [yearStr, monthStr] = selectedMonth.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) {
    return null;
  }
  return new Date(year, month - 1, 1);
};

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatMonthKey = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}`;

const getLastSixMonths = baseDate => {
  const months = [];
  const anchorDate =
    (baseDate && new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)) ||
    new Date();
  for (let i = 5; i >= 0; i--) {
    const date = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - i, 1);
    months.push({
      label: monthNames[date.getMonth()],
      month: date.getMonth() + 1,
      year: date.getFullYear(),
      monthDisplayName: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
    });
  }
  return months;
};

const buildFallbackTrendData = baseDate => {
  const months = getLastSixMonths(baseDate);
  return months.map(monthInfo => ({
    label: monthInfo.label,
    created: 0,
    retired: 0,
    monthDisplayName: monthInfo.monthDisplayName,
  }));
};

const parseChartMonth = monthString => {
  if (!monthString || typeof monthString !== 'string') {
    return null;
  }
  const [yearStr, monthStr] = monthString.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) {
    return null;
  }
  return new Date(year, month - 1, 1);
};

const getCountsFromChartData = (chartData = [], monthInfo) => {
  if (!Array.isArray(chartData) || !chartData.length) return null;
  const key = formatMonthKey(monthInfo.year, monthInfo.month);
  const entry = chartData.find(item => item?.month === key);
  if (!entry) return null;
  return {
    created: Number(entry?.new_items) || 0,
    retired: Number(entry?.retired_items) || 0,
  };
};

const getCountsFromRecentActivity = (summary = {}, monthInfo) => {
  const recentActivity = summary?.recent_activity;
  if (!Array.isArray(recentActivity) || !recentActivity.length) return null;

  const created = recentActivity.filter(activity => {
    if (!activity?.created_at) return false;
    const activityDate = new Date(activity.created_at);
    return (
      activityDate.getMonth() + 1 === monthInfo.month &&
      activityDate.getFullYear() === monthInfo.year
    );
  }).length;

  const retiredStatuses = new Set([
    'retired',
    'decommissioned',
    'out_of_service',
    'retired_item',
  ]);

  const retired = recentActivity.filter(activity => {
    if (!activity?.created_at) return false;
    const activityDate = new Date(activity.created_at);
    const isInMonth =
      activityDate.getMonth() + 1 === monthInfo.month &&
      activityDate.getFullYear() === monthInfo.year;
    return isInMonth && retiredStatuses.has(activity?.status);
  }).length;

  return { created, retired };
};

const NewVsRetiredItems = ({ selectedMonth }) => {
  const [isLifecycleModalOpen, setIsLifecycleModalOpen] = useState(false);
  const [lifecycleTrendData, setLifecycleTrendData] = useState([]);
  const [isLifecycleLoading, setIsLifecycleLoading] = useState(true);
  const [lifecycleError, setLifecycleError] = useState(null);

  const fetchLifecycleData = useCallback(async () => {
    setIsLifecycleLoading(true);
    setLifecycleError(null);
    try {
      const anchorDate = parseSelectedMonthString(selectedMonth);
      const months = getLastSixMonths(anchorDate);

      const lifecycleData = await Promise.all(
        months.map(async monthInfo => {
          try {
            const response = await inventoryDashboardApi.getInventoryDashboard({
              month: String(monthInfo.month),
              year: String(monthInfo.year),
            });

            if (!response?.success || !response.data) {
              throw new Error(response?.error || 'Failed to fetch month data');
            }

            const chartData = response.data?.new_vs_retired_chart?.data;
            const summary =
              response.data?.summary || response.data?.inventory_summary;
            const counts =
              getCountsFromChartData(chartData, monthInfo) ||
              getCountsFromRecentActivity(summary, monthInfo) || {
                created: 0,
                retired: 0,
              };

            return {
              label: monthInfo.label,
              created: counts.created,
              retired: counts.retired,
              monthDisplayName: monthInfo.monthDisplayName,
            };
          } catch (monthError) {
            console.error('Error fetching data for month:', monthInfo, monthError);
            return {
              label: monthInfo.label,
              created: 0,
              retired: 0,
              monthDisplayName: monthInfo.monthDisplayName,
            };
          }
        })
      );

      setLifecycleTrendData(lifecycleData);
    } catch (error) {
      console.error('Error fetching lifecycle data:', error);
      setLifecycleError(error?.message || 'Failed to fetch lifecycle trend data.');
      setLifecycleTrendData(buildFallbackTrendData(parseSelectedMonthString(selectedMonth)));
    } finally {
      setIsLifecycleLoading(false);
    }
  }, [selectedMonth]);

  const lifecycleMaxValue = useMemo(() => {
    if (!lifecycleTrendData.length) return 1;
    return lifecycleTrendData.reduce(
      (max, item) => Math.max(max, item.created, item.retired),
      1
    );
  }, [lifecycleTrendData]);

  const lifecycleLineChartData = useMemo(() => {
    return {
      labels: lifecycleTrendData.map(item => item.label),
      datasets: [
        {
          label: 'Items created',
          data: lifecycleTrendData.map(item => item.created),
          borderColor: '#8BC34A',
          backgroundColor: 'rgba(139, 195, 74, 0.15)',
          fill: true,
          tension: 0.35,
          pointRadius: 4,
          pointBackgroundColor: '#8BC34A',
        },
        {
          label: 'Items retired',
          data: lifecycleTrendData.map(item => item.retired),
          borderColor: '#EF4444',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          fill: true,
          tension: 0.35,
          pointRadius: 4,
          pointBackgroundColor: '#EF4444',
        },
      ],
    };
  }, [lifecycleTrendData]);

  const lifecycleLineChartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: context => {
              const value = context.parsed?.y ?? 0;
              return `${context.dataset.label}: ${value}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: '#E5E7EB',
          },
          ticks: {
            color: '#1F2937',
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: lifecycleMaxValue + 2,
          grid: {
            color: '#E5E7EB',
          },
          ticks: {
            color: '#1F2937',
            callback: value => Number(value).toLocaleString(),
          },
        },
      },
    };
  }, [lifecycleMaxValue]);

  useEffect(() => {
    fetchLifecycleData();
  }, [fetchLifecycleData]);

  useEffect(() => {
    if (!isLifecycleModalOpen) {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isLifecycleModalOpen]);

  return (
    <>
      <div className="flex flex-col gap-4">
        <h2
          className={typography.h3}
          style={{ color: '#000000', marginBottom: '0.5rem' }}
        >
          New vs Retired items (Last 6 months)
        </h2>
        <Card.Gradient className="p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-wrap gap-4">
              <div className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#8BC34A]" />
                <p className={`${typography.p} text-black/70 text-sm`}>
                  Items created
                </p>
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#EF4444]" />
                <p className={`${typography.p} text-black/70 text-sm`}>
                  Items retired
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsLifecycleModalOpen(true)}
              className="p-2 rounded-lg border border-white/60 text-black hover:bg-white/60 transition-colors"
              title="Open fullscreen view"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8 0h3a2 2 0 002-2v-3m0-8V5a2 2 0 00-2-2h-3"
                />
              </svg>
            </button>
          </div>

          <div className="mt-6 h-[260px]">
            {isLifecycleLoading ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="h-10 w-10 border-4 border-t-transparent border-blue-500 rounded-full animate-spin" />
                <p className={typography.p} style={{ color: '#333333' }}>
                  Loading chart data...
                </p>
              </div>
            ) : lifecycleError ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                <p className={typography.p} style={{ color: '#DC2626' }}>
                  {lifecycleError}
                </p>
                <button
                  type="button"
                  onClick={fetchLifecycleData}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : !lifecycleTrendData.length ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <p className={typography.p} style={{ color: '#666666' }}>
                  No data available
                </p>
                <p
                  className={typography.p}
                  style={{ color: '#999999', fontSize: '0.875rem' }}
                >
                  Chart data will appear when inventory data is available
                </p>
              </div>
            ) : (
              <Line data={lifecycleLineChartData} options={lifecycleLineChartOptions} />
            )}
          </div>
        </Card.Gradient>
      </div>

      {isLifecycleModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur">
            <div className="bg-white rounded-3xl w-full max-w-5xl mx-4 p-6 relative shadow-2xl">
              <button
                type="button"
                onClick={() => setIsLifecycleModalOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-black/5 hover:bg-black/10 transition-colors"
                aria-label="Close full-screen chart"
              >
                <svg
                  className="w-5 h-5 text-black"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <h2
                className={typography.h3}
                style={{ color: '#000000', marginBottom: '0.5rem' }}
              >
                New vs Retired items (Last 6 months)
              </h2>
              <p className={`${typography.p} text-black/70`}>
                Monthly trend of items added and retired.
              </p>
              <div className="mt-6 h-[520px]">
                {isLifecycleLoading ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <div className="h-10 w-10 border-4 border-t-transparent border-blue-500 rounded-full animate-spin" />
                    <p className={typography.p} style={{ color: '#333333' }}>
                      Loading chart data...
                    </p>
                  </div>
                ) : lifecycleError ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                    <p className={typography.p} style={{ color: '#DC2626' }}>
                      {lifecycleError}
                    </p>
                    <button
                      type="button"
                      onClick={fetchLifecycleData}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                ) : !lifecycleTrendData.length ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                    <svg
                      className="w-12 h-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    <p className={typography.p} style={{ color: '#666666' }}>
                      No data available
                    </p>
                    <p
                      className={typography.p}
                      style={{ color: '#999999', fontSize: '0.875rem' }}
                    >
                      Chart data will appear when inventory data is available
                    </p>
                  </div>
                ) : (
                  <Line
                    data={lifecycleLineChartData}
                    options={lifecycleLineChartOptions}
                  />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default NewVsRetiredItems;

NewVsRetiredItems.propTypes = {
  selectedMonth: PropTypes.string,
};
